import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';

import { CallController } from '@gem/call/CallController/CallController';
import {
  ICallAddSpeakerParams,
  IOnDeleteCallParams,
} from '@gem/call/CallController/CallController.types';
import { Call } from '@gem/call/CallStore/Call';
import { CallView } from '@gem/call/CallView/CallView';
import { ICallContext } from '@gem/call/CallView/CallView.context';
import { getUnionCallLink } from '@gem/call/Model/callModel';
import { CallProviderType } from '@gem/call/Model/callModel.types';
import { wsCallsApiConnect, wsCallsApiDisconnect } from '@gem/call/WsApi/wsApi';
import { disposeCallsApi } from '@gem/call/WsApi/wsCallsApi/WsCallsApi';
import { useTranslation } from '@gem/modules/i18n';
import { PopupStore } from '@gem/popups';

import Tasks from 'APP/Tasks';
import { refreshToken } from 'APP/Tasks/authorization/authorization';
import { RefreshTokenInitiator } from 'APP/Tasks/authorization/login/login.types';
import { AudioSourceType } from 'APP/constants/app';
import { ApplicationTheme } from 'APP/constants/appearance';
import { ForwardMessageWithInfoPopup } from 'APP/main/PopupManager/Popups/ForwardMessageWithInfoPopup/ForwardMessageWithInfoPopup';
import { ProjectUrls } from 'APP/packages/projectUrls/ProjectUrls';
import { storage } from 'APP/packages/storage';
import { websocketConfig } from 'APP/wsApi/wsApi.config';
import Entities from 'STORE';
import { MainCall } from 'STORE/Calls/MainCall/MainCall';

import { CallSelectSpeakersPopup } from '../PopupManager/Popups/CallSelectSpeakersPopup/CallSelectSpeakersPopup';

interface IAuthorizedOngoingCallProps {
  mainCall: MainCall;
}

export const AuthorizedOngoingCall = observer<IAuthorizedOngoingCallProps>(({ mainCall }) => {
  const { t } = useTranslation();
  const group = mainCall.groupId ? Entities.GroupsStore.getGroupById(mainCall.groupId) : null;
  const [state, setState] = useState<ICallContext>({} as ICallContext);
  const groupRoute = group?.routePath;
  const isStarted = useRef(false);
  const isSocketInitialized = useRef(false);

  const onDeleteCall = ({ isInternalFinish }: IOnDeleteCallParams): void => {
    Tasks.app.audioSource.removeSource(AudioSourceType.Call);
    if (isSocketInitialized.current) {
      wsCallsApiDisconnect();
    }
    if (mainCall && isInternalFinish) {
      mainCall.setDeleteCallHandler(null);
      Entities.Calls.delete(mainCall.groupId, mainCall.channelId);
    }
  };

  const startCallsSocket = (channelId: string): void => {
    if (!isSocketInitialized.current) {
      wsCallsApiConnect({
        session: websocketConfig.token || '',
        channelId,
        wsCallsUrl: ProjectUrls.wsCallsUrl,
        refreshToken: async () => onRefreshToken(),
      });
      isSocketInitialized.current = true;
      if (state.callController) {
        state.callController.initCallStateService(channelId);
      }
    }
  };

  const onRefreshToken = async (): Promise<string | null> => {
    const result = await refreshToken(false, RefreshTokenInitiator.WsCalls);

    if (result.isSuccess && result.token) {
      return result.token;
    } else {
      return null;
    }
  };

  const onAddCallMembers = async (call: Call): Promise<void> => {
    let inviteLink = call.inviteLink;

    if (!inviteLink && call.channelId) {
      inviteLink = await getUnionCallLink({ channelId: call.channelId });
      call.setInviteLink(inviteLink);
    }

    Tasks.app.openPopup(ForwardMessageWithInfoPopup, {
      params: {
        title: t('common_send_link'),
        info: inviteLink || '',
        showToastCallback: () => {
          Tasks.app.showToast(t('call_link_sent_popup'));
        },
      },
    });
  };

  const onAddCallSpeakers = (params: ICallAddSpeakerParams): PopupStore => {
    return Tasks.app.openPopup(CallSelectSpeakersPopup, { params });
  };

  useEffect(() => {
    const call = new Call({
      myId: mainCall.myId,
      groupId: mainCall.groupId || undefined,
      channelId: mainCall.channelId,
      providerType: CallProviderType.Livekit,
      initialDevicesIds: mainCall.initialDevicesIds,
      mediaPermissions: mainCall.mediaPermissions,
      maxParticipants: Entities.Calls.maxParticipants,
      initialMeOpponentAvatar: {
        title: Entities.usersStore.Me.avatarTitle,
        url: Entities.usersStore.Me.avatarUrl,
        colorIndex: Entities.usersStore.Me.avatarColorIndex,
      },
    });

    const callController = new CallController({
      call,
      providerUrl: ProjectUrls.liveKitCallsUrl,
      handlers: {
        feedbackHandler: Tasks.feedback.checkStarsFeedback,
        onDeleteCall,
        onAddCallMembers,
        onAddCallSpeakers,
      },
    });

    if (mainCall.waitingRoomMembers && mainCall.waitingRoomMembers.length) {
      call.waitingRoom.setRequested(mainCall.waitingRoomMembers);
    }
    if (mainCall.isWaitingRoomEnabled) {
      call.waitingRoom.setEnabled(mainCall.isWaitingRoomEnabled);
    }
    call.setGuestsAllowed(mainCall.isGuestsAllowed);
    call.viewState.expand();

    setState({ call, callController });
    mainCall.setDeleteCallHandler(async () => {
      if (callController) {
        await callController.dispose();
      }
    });

    return (): void => {
      disposeCallsApi();
      if (state.call) {
        state.call.dispose();
      }
    };
  }, []);

  useEffect(() => {
    if (mainCall.channelId) {
      startCallsSocket(mainCall.channelId);
    }
  }, [mainCall.channelId]);

  useEffect(() => {
    if (state.call?.isStarted && !mainCall?.isStarted) {
      mainCall?.setStarted(true);
    }
    if (state.call?.startTs && !mainCall?.startTs) {
      mainCall?.setStartTs(state.call.startTs);
    }
  }, [state.call?.isStarted, state.call?.startTs]);

  useEffect(() => {
    if (mainCall.readyToStart && mainCall.callInfo && state.call && !isStarted.current) {
      isStarted.current = true;
      startCallsSocket(mainCall.callInfo.channelId);

      state.callController.startCall({
        callInfo: mainCall.callInfo,
        withoutTimer: mainCall.withoutTimer,
        isVideoMuted: !mainCall.initialMedia.withVideo,
        isAudioMuted: !mainCall.initialMedia.withAudio,
      });
    }
  }, [mainCall.readyToStart, state.call]);

  useEffect(() => {
    if (group && state.call) {
      state.call.setAvatar({
        title: group.avatarTitle,
        url: group.avatarUrl,
        colorIndex: group.avatarColorIndex,
      });
    }
  }, [state.call, group]);

  useEffect(() => {
    if (!state.call) {
      return;
    }
    if (state.call.viewState.isFullScreen) {
      Tasks.app.fullscreen.registerFullscreen(state.call.viewState.collapse);
    } else {
      Tasks.app.fullscreen.unregisterFullscreen(state.call.viewState.collapse);
    }

    return (): void => {
      Tasks.app.fullscreen.unregisterFullscreen(state.call.viewState.collapse);
    };
  }, [state.call?.viewState.isFullScreen]);

  useEffect(() => {
    if (!state.call) {
      return;
    }

    const theme = storage.theme.get();

    if (state.call.viewState.isFullScreen && theme === ApplicationTheme.Dark) {
      Entities.appearance.setTheme(ApplicationTheme.Light);
    } else if (!state.call.viewState.isFullScreen && theme !== Entities.appearance.theme) {
      Entities.appearance.setTheme(theme);
    }

    return (): void => {
      // Restore the theme when the component unmounts (call ends)
      const savedTheme = storage.theme.get();
      if (savedTheme && Entities.appearance.theme !== savedTheme) {
        Entities.appearance.setTheme(savedTheme);
      }
    };
  }, [state.call?.viewState.isFullScreen]);

  if (!state.call) {
    return null;
  }

  return (
    <CallView
      state={state}
      groupRoute={groupRoute}
    />
  );
});
